import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styled from 'styled-components';
import { 
  UserIcon,
  CalendarIcon,
  GlobeIcon,
  IdCardIcon,
  UploadIcon as UploadIconComponent,
  SpinnerIcon,
  CameraIcon
} from '../../utils/IconWrapper';
import { registrationPhase2Schema, registerPhase2, uploadIdentityDocument } from '../../utils/supabaseExtended';
import { z } from 'zod';

const StepContainer = styled.div`
  padding: 1rem 0;
`;

const StepTitle = styled.h2`
  font-size: 1.5rem;
  color: #004744;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StepDescription = styled.p`
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
  max-width: 600px;
  margin: 0 auto;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-left: 2.5rem;
  border: 2px solid ${props => props.hasError ? '#ef4444' : 'var(--border-color)'};
  border-radius: 8px;
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ef4444' : '#004744'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(0, 71, 68, 0.1)'};
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-left: 2.5rem;
  border: 2px solid ${props => props.hasError ? '#ef4444' : 'var(--border-color)'};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ef4444' : '#004744'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(0, 71, 68, 0.1)'};
  }
  
  option {
    background-color: var(--bg-primary);
    color: var(--text-primary);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
`;

const DocumentSection = styled.div`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1rem 0;
  border: 2px dashed var(--border-color);
`;

const DocumentTitle = styled.h3`
  color: #004744;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DocumentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const DocumentUpload = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const UploadArea = styled.label<{ uploaded?: boolean; uploading?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 150px;
  border: 2px dashed ${props => props.uploaded ? '#004744' : 'var(--border-color)'};
  border-radius: 8px;
  background: ${props => props.uploaded ? 'rgba(0, 71, 68, 0.1)' : 'var(--bg-secondary)'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #004744;
    background: rgba(0, 71, 68, 0.1);
  }
  
  ${props => props.uploading && `
    pointer-events: none;
    opacity: 0.7;
  `}
`;

const UploadIcon = styled.div<{ uploaded?: boolean }>`
  font-size: 2rem;
  color: ${props => props.uploaded ? '#004744' : 'var(--text-secondary)'};
  margin-bottom: 0.5rem;
`;

const UploadText = styled.span<{ uploaded?: boolean }>`
  color: ${props => props.uploaded ? '#004744' : 'var(--text-secondary)'};
  font-size: 0.9rem;
  text-align: center;
`;

const HiddenInput = styled.input`
  display: none;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 0.5rem;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: 1rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary'; loading?: boolean }>`
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  flex: 1;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #004744 0%, #004744 100%);
    color: white;
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(0, 71, 68, 0.4);
    }
  ` : `
    background: #f9fafb;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f3f4f6;
    }
  `}
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  border-left: 4px solid #f59e0b;
`;

const InfoText = styled.p`
  color: #92400e;
  margin: 0;
  font-size: 0.9rem;
`;

type FormData = z.infer<typeof registrationPhase2Schema>;

interface RegistrationStep2Props {
  onNext: (data: FormData) => void;
  onPrevious: () => void;
  initialData?: Partial<FormData>;
}

interface UploadState {
  documentFront?: string;
  documentBack?: string;
  selfie?: string;
}

const RegistrationStep2: React.FC<RegistrationStep2Props> = ({
  onNext,
  onPrevious,
  initialData
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>({});
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const documentFrontRef = useRef<HTMLInputElement>(null);
  const documentBackRef = useRef<HTMLInputElement>(null);
  const selfieRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<FormData>({
    resolver: zodResolver(registrationPhase2Schema),
    defaultValues: initialData
  });

  const documentType = watch('documentType');

  const handleFileUpload = async (
    file: File,
    type: 'document_front' | 'document_back' | 'selfie'
  ) => {
    setUploadingDoc(type);
    
    try {
      const result = await uploadIdentityDocument(file, type);
      
      if (result.success && result.url) {
        setUploadState(prev => ({
          ...prev,
          [type]: result.url
        }));
      } else {
        alert(result.error || 'Errore durante l\'upload');
      }
    } catch (error) {
      alert('Errore durante l\'upload del documento');
    } finally {
      setUploadingDoc(null);
    }
  };

  const onSubmit = async (data: FormData) => {
    // Rimuovo la validazione obbligatoria dei documenti
    // Gli utenti possono ora continuare senza caricare i documenti
    setIsLoading(true);
    setSubmitError(null);

    try {
      const result = await registerPhase2(data);
      
      if (result.success) {
        onNext(data);
      } else {
        setSubmitError(result.error || 'Errore durante l\'aggiornamento del profilo');
      }
    } catch (error) {
      setSubmitError('Errore di connessione. Riprova più tardi.');
    } finally {
      setIsLoading(false);
    }
  };

  // Nuova funzione per saltare la verifica documenti
  const handleSkipDocuments = async () => {
    const formData = watch();
    
    // Valida solo i campi obbligatori (non i documenti)
    if (!formData.fullName || !formData.dateOfBirth || !formData.nationality) {
      setSubmitError('Completa i dati personali obbligatori prima di continuare');
      return;
    }

    setIsLoading(true);
    setSubmitError(null);

    try {
      const result = await registerPhase2(formData);
      
      if (result.success) {
        onNext(formData);
      } else {
        setSubmitError(result.error || 'Errore durante l\'aggiornamento del profilo');
      }
    } catch (error) {
      setSubmitError('Errore di connessione. Riprova più tardi.');
    } finally {
      setIsLoading(false);
    }
  };

  const nationalities = [
    'Italiana', 'Francese', 'Tedesca', 'Spagnola', 'Britannica', 'Americana', 'Canadese',
    'Australiana', 'Svizzera', 'Austriaca', 'Belga', 'Olandese', 'Danese', 'Svedese',
    'Norvegese', 'Finlandese', 'Polacca', 'Ceca', 'Ungherese', 'Rumena', 'Bulgara',
    'Greca', 'Portoghese', 'Russa', 'Ucraina', 'Brasiliana', 'Argentina', 'Messicana',
    'Giapponese', 'Cinese', 'Indiana', 'Altra'
  ];

  return (
    <StepContainer>
      <StepTitle>Completa il tuo profilo</StepTitle>
      <StepDescription>
        Aggiungi i tuoi dati personali e verifica la tua identità per prenotare
      </StepDescription>

      <InfoBox>
        <InfoText>
          ⚡ <strong>Perché verificare l'identità?</strong> La verifica dell'identità ci permette di garantire sicurezza e fiducia nella nostra comunità, proteggendo sia host che ospiti. <strong>Puoi saltare questo passaggio e verificare i documenti in seguito dal tuo profilo.</strong>
        </InfoText>
      </InfoBox>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormGroup>
            <Label>
              <UserIcon />
              Nome completo
            </Label>
            <InputWrapper>
              <InputIcon>
                <UserIcon />
              </InputIcon>
              <Input
                type="text"
                placeholder="Mario Rossi"
                hasError={!!errors.fullName}
                {...register('fullName')}
              />
            </InputWrapper>
            {errors.fullName && <ErrorMessage>{errors.fullName.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>
              <CalendarIcon />
              Data di nascita
            </Label>
            <InputWrapper>
              <InputIcon>
                <CalendarIcon />
              </InputIcon>
              <Input
                type="date"
                hasError={!!errors.dateOfBirth}
                {...register('dateOfBirth')}
              />
            </InputWrapper>
            {errors.dateOfBirth && <ErrorMessage>{errors.dateOfBirth.message}</ErrorMessage>}
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <Label>
              <GlobeIcon />
              Nazionalità
            </Label>
            <InputWrapper>
              <InputIcon>
                <GlobeIcon />
              </InputIcon>
              <Select
                hasError={!!errors.nationality}
                {...register('nationality')}
              >
                <option value="">Seleziona nazionalità</option>
                {nationalities.map(nat => (
                  <option key={nat} value={nat}>{nat}</option>
                ))}
              </Select>
            </InputWrapper>
            {errors.nationality && <ErrorMessage>{errors.nationality.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>
              <IdCardIcon />
              Tipo documento
            </Label>
            <InputWrapper>
              <InputIcon>
                <IdCardIcon />
              </InputIcon>
              <Select
                hasError={!!errors.documentType}
                {...register('documentType')}
              >
                <option value="">Seleziona tipo documento</option>
                <option value="carta_identita">Carta d'Identità</option>
                <option value="passaporto">Passaporto</option>
                <option value="patente">Patente di Guida</option>
              </Select>
            </InputWrapper>
            {errors.documentType && <ErrorMessage>{errors.documentType.message}</ErrorMessage>}
          </FormGroup>
        </FormRow>

        {/* Indirizzo opzionale */}
        <FormGroup>
          <Label>
            Indirizzo (opzionale)
          </Label>
          <InputWrapper>
            <Input
              type="text"
              placeholder="Via Roma 123"
              {...register('addressStreet')}
            />
          </InputWrapper>
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Label>
              Città
            </Label>
            <Input
              type="text"
              placeholder="Milano"
              {...register('addressCity')}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              CAP
            </Label>
            <Input
              type="text"
              placeholder="20100"
              {...register('addressPostalCode')}
            />
          </FormGroup>
        </FormRow>

        {/* Sezione upload documenti - ora opzionale */}
        <DocumentSection>
          <DocumentTitle>
            <IdCardIcon />
            Verifica dell'identità (opzionale)
          </DocumentTitle>
          
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            <strong>📋 Opzionale:</strong> Carica le foto del tuo {documentType === 'carta_identita' ? 'documento d\'identità' : 
            documentType === 'passaporto' ? 'passaporto' : 
            documentType === 'patente' ? 'patente di guida' : 'documento'} e un selfie per la verifica. 
            Potrai fare questo passaggio anche in seguito dal tuo profilo utente.
          </p>

          <DocumentGrid>
            <DocumentUpload>
              <UploadArea 
                uploaded={!!uploadState.documentFront}
                uploading={uploadingDoc === 'document_front'}
              >
                {uploadState.documentFront ? (
                  <>
                    <PreviewImage src={uploadState.documentFront} alt="Documento fronte" />
                    <UploadText uploaded>Fronte caricato ✓</UploadText>
                  </>
                ) : (
                  <>
                    <UploadIcon>
                                             {uploadingDoc === 'document_front' ? <SpinnerIcon /> : <UploadIconComponent />}
                    </UploadIcon>
                    <UploadText>
                      {uploadingDoc === 'document_front' ? 'Caricamento...' : 'Fronte documento'}
                    </UploadText>
                  </>
                )}
                <HiddenInput
                  ref={documentFrontRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'document_front');
                  }}
                />
              </UploadArea>
            </DocumentUpload>

            <DocumentUpload>
              <UploadArea 
                uploaded={!!uploadState.documentBack}
                uploading={uploadingDoc === 'document_back'}
              >
                {uploadState.documentBack ? (
                  <>
                    <PreviewImage src={uploadState.documentBack} alt="Documento retro" />
                    <UploadText uploaded>Retro caricato ✓</UploadText>
                  </>
                ) : (
                  <>
                    <UploadIcon>
                                             {uploadingDoc === 'document_back' ? <SpinnerIcon /> : <UploadIconComponent />}
                    </UploadIcon>
                    <UploadText>
                      {uploadingDoc === 'document_back' ? 'Caricamento...' : 'Retro documento'}
                    </UploadText>
                  </>
                )}
                <HiddenInput
                  ref={documentBackRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'document_back');
                  }}
                />
              </UploadArea>
            </DocumentUpload>

            <DocumentUpload>
              <UploadArea 
                uploaded={!!uploadState.selfie}
                uploading={uploadingDoc === 'selfie'}
              >
                {uploadState.selfie ? (
                  <>
                    <PreviewImage src={uploadState.selfie} alt="Selfie" />
                    <UploadText uploaded>Selfie caricato ✓</UploadText>
                  </>
                ) : (
                  <>
                    <UploadIcon>
                      {uploadingDoc === 'selfie' ? <SpinnerIcon /> : <CameraIcon />}
                    </UploadIcon>
                    <UploadText>
                      {uploadingDoc === 'selfie' ? 'Caricamento...' : 'Selfie'}
                    </UploadText>
                  </>
                )}
                <HiddenInput
                  ref={selfieRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'selfie');
                  }}
                />
              </UploadArea>
            </DocumentUpload>
          </DocumentGrid>
        </DocumentSection>

        {submitError && (
          <ErrorMessage style={{ textAlign: 'center', fontSize: '1rem' }}>
            {submitError}
          </ErrorMessage>
        )}

        <NavigationButtons>
          <Button type="button" variant="secondary" onClick={onPrevious}>
            Indietro
          </Button>
          
          <Button 
            type="button" 
            variant="secondary" 
            onClick={handleSkipDocuments}
            disabled={isLoading}
            style={{ 
              background: '#f59e0b', 
              color: 'white',
              border: 'none'
            }}
          >
            {isLoading && <LoadingSpinner />}
            Salta per ora
          </Button>
          
          <Button type="submit" variant="primary" disabled={isLoading} loading={isLoading}>
            {isLoading && <LoadingSpinner />}
            Continua con verifica
          </Button>
        </NavigationButtons>
      </Form>
    </StepContainer>
  );
};

export default RegistrationStep2; 